// Port of Sheep Tag onboarding StepFlow and coach rules: one action, a success beat,
// first three matches, calm moments only, and no repeats of the HUD's announcements.
import type { Command } from '../../sim/sim';
export type Input = 'touch' | 'mouse';
export function tipsInput(pointer: string | undefined, coarse: boolean, fine = !coarse): Input {
  return pointer === 'mouse' && fine ? 'mouse' : pointer === 'touch' || pointer === 'pen' || coarse ? 'touch' : 'mouse';
}
export const STEPS = ['march', 'send', 'select', 'tier', 'skill', 'retreat'] as const;
export type Step = typeof STEPS[number];
export type Tip = 'salve' | 'group';
export type Saved = { done: boolean; matches: number; counts: Record<Tip, number> };
export const emptySaved = (): Saved => ({ done: false, matches: 0, counts: { salve: 0, group: 0 } });
export type Guide = { index: number; beat: number; ended: boolean };
export const startGuide = (): Guide => ({ index: 0, beat: 0, ended: false });
export function advance(g: Guide, action: Step | 'skip' | null, dt = 0): Guide {
  if (action === 'skip') return { ...g, ended: true };
  if (g.ended) return g;
  if (g.beat > 0) {
    const beat = Math.max(0, g.beat - dt);
    return beat ? { ...g, beat } : { index: g.index + 1, beat: 0, ended: g.index + 1 === STEPS.length };
  }
  return action === STEPS[g.index] ? { ...g, beat: .9 } : g;
}
export function stepLine(step: Step, input: Input, compact = false) {
  const touch = input === 'touch';
  if (compact) return ({ march: 'Soldiers march automatically.', send: touch ? 'Tap Center to send.' : 'Click Center to send.',
    select: touch ? 'Hold, drag to select.' : 'Drag to select soldiers.', tier: 'Buy your next tier.',
    skill: touch ? 'Tap your first skill.' : 'Press Q for your skill.', retreat: touch ? 'Select, then tap Defend.' : 'Select, then press R.' })[step];
  switch (step) {
    case 'march': return 'Watch soldiers march from home.';
    case 'send': return touch ? 'Tap Center to send soldiers.' : 'Click Center to send soldiers.';
    case 'select': return touch ? 'Hold, then drag to select soldiers.' : 'Drag to select soldiers.';
    case 'tier': return 'Buy your next tier.';
    case 'skill': return touch ? "Tap your first skill." : "Press Q for your first skill.";
    case 'retreat': return touch ? 'Select soldiers, then tap Defend.' : 'Select soldiers, then press R.';
  }
}
/** folded: a portrait phone keeps groups 1 to 4 behind its Groups button (controls.ts fan), where a hold does nothing,
 *  so the group tip names the tap that shows them first (review of feat/hud-bc F2). */
export function tipLine(t: Tip, compact = false, folded = false) {
  if (t === 'group' && folded) return compact ? 'Tap Groups, then hold one.' : 'Tap Groups, then hold a group to send recruits there.';
  if (compact) return t === 'salve' ? 'Hurt? Shop for Healing Salve.' : 'Hold a group to set recruits.';
  return t === 'salve' ? 'Buy Healing Salve at the center Shop.' : 'Hold a group to send recruits there.';
}
/** Whether a tip counts as seen once read. The salve tip does; the group tip counts only when a hold opens a group's
 *  card (GuideView.acted), since reading "hold a group" teaches nothing until the hold is found (review F2). */
export const countsOnRead = (t: Tip) => t === 'salve';
export function chooseTip(s: Saved, f: { enabled: boolean; guiding: boolean; fighting: boolean; busy: boolean; salve: boolean; group: boolean }): Tip | null {
  if (!f.enabled || f.guiding || f.fighting || f.busy || s.matches > 3) return null;
  return (['salve', 'group'] as const).find(t => f[t] && s.counts[t] < 1) ?? null;
}
export function countTip(s: Saved, t: Tip): Saved { return { ...s, counts: { ...s.counts, [t]: Math.min(1, s.counts[t] + 1) } }; }
export function parseTips(raw: string | null): Saved {
  const s = emptySaved();
  if (raw === '1') return { ...s, done: true }; // retired card's flag
  try {
    const v = JSON.parse(raw ?? 'null');
    if (v && typeof v === 'object') {
      s.done = v.done === true;
      s.matches = Number.isFinite(v.matches) ? Math.max(0, Math.floor(v.matches)) : 0;
      for (const t of ['salve', 'group'] as const) s.counts[t] = v.counts?.[t] >= 1 ? 1 : 0;
    }
  } catch { /* corrupt storage starts fresh */ }
  return s;
}

/** Conditions hide a lesson without advancing it. Only its accepted action advances. */
export function guideReady(step: Step, f: { enabled: boolean; fighting: boolean; busy: boolean; tier: boolean; skill: boolean; selected: boolean; fightSeen: boolean }, beat = false) {
  if (!f.enabled) return false;
  if (beat) return true;
  if (f.busy) return false;
  if (step === 'tier') return f.tier;
  if (step === 'skill') return f.skill;
  if (step === 'retreat') return f.fightSeen && f.fighting;
  return true;
}

/** Search both axes; the centered column alone may be blocked by a lifted HUD. */
export function placeGuide(W: number, H: number, w: number, h: number,
  obstacles: readonly { left: number; right: number; top: number; bottom: number }[],
  accepts: (x: number, y: number) => boolean = () => true) {
  const xs = [(W - w) / 2, 8, W - w - 8, ...obstacles.flatMap(r => [r.left - w - 6, r.right + 6])];
  for (let y = 8; y + h <= H - 8; y += 4) for (const x of xs) {
    if (x < 8 || x + w > W - 8) continue;
    if (!obstacles.some(r => x < r.right + 6 && x + w > r.left - 6 && y < r.bottom + 6 && y + h > r.top - 6) && accepts(x, y)) return { x, y };
  }
  return null;
}

/** The arrow art's tip sits this share of its size ahead of its center (guide.ts's path ends at x 29 of 32). */
export const TIP = 13 / 32;
/** The resting tip stops this far short of its target; the 8px bob closes most of it. */
export const GAP = 10;
type Box = { left: number; right: number; top: number; bottom: number };
const near = (r: Box, x: number, y: number, m: number) => x + m > r.left && x - m < r.right && y + m > r.top && y - m < r.bottom;
/** A point the player can see: inside the window and not under the HUD or the card. */
export const inView = (W: number, H: number, p: { x: number; y: number }, covers: readonly Box[]) =>
  p.x >= 0 && p.x <= W && p.y >= 0 && p.y <= H && !covers.some(r => near(r, p.x, p.y, 0));

/** The tip lands on the target or there is no arrow (Devin 2026-10-30: arrows aimed along a ray at an off-screen
 *  plaza floated over trees). A control is pointed at from beside it; a world point only while it is on screen and
 *  clear of the HUD, from whichever side faces the card. The whole rotated arrow and its bob stay off the card and
 *  every other control; a crowded control has no arrow and keeps its pulse. */
export function placeArrow(W: number, H: number, size: number,
  card: { left: number; top: number; width: number; height: number },
  target: { x: number; y: number; bounds?: Box },
  obstacles: readonly Box[]) {
  const r = target.bounds, pad = size / Math.SQRT2 + 8, reach = size * TIP + GAP;
  const cardBox = { left: card.left, right: card.left + card.width, top: card.top, bottom: card.top + card.height };
  // A control's own box and the rows holding it are what the arrow points into, not what it must avoid.
  const blocked = [cardBox, ...obstacles.filter(o => !r || !(o.left <= r.left && o.right >= r.right && o.top <= r.top && o.bottom >= r.bottom))];
  if (!Number.isFinite(target.x + target.y) || !inView(W, H, target, r ? [cardBox] : blocked)) return null;
  // Where the arrow can point from: above, left of, right of or below a control; around a world point, the card's side first.
  const toward = Math.atan2(target.y - (cardBox.top + cardBox.bottom) / 2, target.x - (cardBox.left + cardBox.right) / 2);
  const angles = r ? [Math.PI / 2, 0, Math.PI, -Math.PI / 2]
    : Array.from({ length: 16 }, (_, k) => toward + (k % 2 ? 1 : -1) * Math.ceil(k / 2) * Math.PI / 8);
  for (const angle of angles) {
    const c = Math.cos(angle), s = Math.sin(angle);
    const x = r ? (c > .5 ? r.left : c < -.5 ? r.right : target.x) - c * reach : target.x - c * reach;
    const y = r ? (s > .5 ? r.top : s < -.5 ? r.bottom : target.y) - s * reach : target.y - s * reach;
    if (x < pad || x > W - pad || y < pad || y > H - pad || blocked.some(o => near(o, x, y, pad))) continue;
    return { x, y, angle, side: Math.abs(c) > Math.abs(s) ? c > 0 ? 'right' : 'left' : s > 0 ? 'bottom' : 'top' };
  }
  return null;
}

/** The guide step a successful command completes. Center sends the whole army, or the selection, to the plaza. */
export function commandStep(c: Command): Step | null {
  if (c.t === 'cast' && c.k === 0) return 'skill';
  if (c.t === 'tier') return 'tier';
  if ((c.t === 'goal' || (c.t === 'order' && !c.move)) && c.goal?.kind === 'center') return 'send';
  return null;
}
